import { supabase } from '../api/supabase'

// ONE definition of "which address does this party use on this job", answered by the database function
// resolve_job_party_address (migration 20261024b) so the screen, the BoxTop sync, the invoice PDF and eCustoms cannot disagree.
export type PartyAddressSource =
  | 'chosen'          // staff picked it on this job (link + snapshot)
  | 'default'         // the company's default when the company was put on the job (link + snapshot)
  | 'provisional'     // nothing stored on this job yet: the company's CURRENT default, shown but not final
  | 'legacy_columns'  // last resort: the old customers.address_* columns -- the address book has nothing
  | 'none'

export interface ResolvedPartyAddress {
  customer_id: string | null
  address_id: string | null
  source: PartyAddressSource
  reason: string
  address_lines: string[] | null
  city: string | null
  county: string | null
  postcode: string | null
  country: string | null
  contact_name: string | null
  contact_phone: string | null
  contact_email: string | null
  roles: string[] | null
}

export type JobPartyRole =
  | 'importer' | 'consignor' | 'notify' | 'hbl_importer' | 'hbl_consignor' | 'hbl_notify'
  | 'invoice_to' | 'overseas_agent' | 'port_agent'

export async function resolveJobPartyAddress(jobId: string, role: JobPartyRole): Promise<ResolvedPartyAddress | null> {
  const { data, error } = await supabase.rpc('resolve_job_party_address', { p_job_id: jobId, p_party_role: role })
  if (error) throw new Error(error.message)
  return ((data as ResolvedPartyAddress[] | null) ?? [])[0] ?? null
}

export async function setJobPartyAddress(jobId: string, role: JobPartyRole, addressId: string, userEmail: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_job_party_address', { p_job_id: jobId, p_party_role: role, p_address_id: addressId, p_user: userEmail })
  if (error) throw new Error(error.message)
}

// "line1, line2, city, county, postcode, country" -- the same one-line text everywhere an address is shown.
export function formatResolvedAddress(a: Pick<ResolvedPartyAddress, 'address_lines' | 'city' | 'county' | 'postcode' | 'country'>): string {
  return [...(a.address_lines ?? []), a.city, a.county, a.postcode, a.country && a.country !== 'GB' ? a.country : null]
    .filter(x => x && String(x).trim().length > 0).join(', ')
}

// BoxTop sync and anything else that is permanent needs an address that was actually recorded on the job, not a guess.
export const isRecordedPartyAddress = (a: ResolvedPartyAddress | null | undefined): boolean => a?.source === 'chosen' || a?.source === 'default'
